import _ from 'lodash';
import React from 'react';

import {
  Check,
  Close
} from '@mui/icons-material';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField
} from '@mui/material';
import {
  appFormUtil,
  appValidationUtil, dialogUtil, SystemAlert
} from 'modules';

import update from 'immutability-helper';
import * as dataService from './dataService';
import * as types from './types';



const {
  close,
  open,
  useTrigger
} = dialogUtil.makeTrigger<{
  lines: types.IItemReceipt[];
}>({
  lines: []
});

export {
  close,
  open
};



export type IItemReceipt = types.IItemReceipt;



export const FC: React.FC = () => {
  const triggerState = useTrigger();

  const {
    formState,
    dispatch,
    genFieldProps
  } = appFormUtil.useFormState<types.IFormState>({
    initialState: {
      lines: triggerState.lines,
      serialNumber: ''
    },
    key2FieldConfig: {
      serialNumber: {
        fnValidate: value => {
          if (!appValidationUtil.reImei.test(value as string)) {
            return "Not a valid IMEI";
          }

          return "";
        }
      }
    }
  });



  React.useEffect(() => {
    dispatch({
      type: 'BATCH_UPDATE',
      payload: {
        lines: triggerState.lines,
        serialNumber: ''
      }
    });
  }, [
    dispatch,
    triggerState.lines
  ]);



  const [receiveReturnAuthorization, receiveReturnAuthorizationTuple] = dataService.useReceiveReturnAuthorization();



  const allReceived = React.useMemo(
    () => formState.lines.every(item => item.isReceived),
    [formState.lines]
  );

  const handleSubmit = React.useCallback(
    async () => {
      if (!allReceived) {
        window.alert("Every item must be marked as received in order to continue.");
        return;
      }
      await receiveReturnAuthorization(formState);
      SystemAlert.sendAlert({
        severity: "success",
        message: `${formState.lines.length} RMAs received.`
      });
      close();
    },
    [
      allReceived,
      formState,
      receiveReturnAuthorization
    ]
  );



  const { value, error, onChange } = genFieldProps('serialNumber');

  React.useEffect(
    () => {
      if (error) {
        return;
      }
      const foundIndex = _.findIndex(formState.lines, line => line.serialNumber === value);
      if (foundIndex > -1) {
        dispatch({
          type: 'BATCH_UPDATE',
          payload: {
            serialNumber: '',
            lines: update(formState.lines, {
              [foundIndex]: {
                isReceived: {
                  $set: true
                }
              }
            })
          }
        });

      } else {
        window.alert("That IMEI was not found in this pre-alert.");
        dispatch({
          type: 'UPDATE',
          payload: {
            property: 'serialNumber',
            value: ''
          }
        });
      }
    },
    [dispatch, formState.lines, error, value]
  );


  return (
    <Dialog
      open={!_.isEmpty(triggerState.lines)}
      onClose={close}>
      <DialogTitle>
        Bulk Receive
      </DialogTitle>

      <DialogContent>
        <Box sx={{
          pt: 1,
          display: 'flex',
          justifyContent: 'space-between'
        }}>
          <TextField
            autoFocus
            inputProps={{
              ref: (node?: HTMLInputElement) => node?.focus(),
              autoFocus: true
            }}

            label="Serial Number"
            disabled={allReceived || receiveReturnAuthorizationTuple.loading}
            value={value}
            onChange={onChange} />

          <Button
            disabled={receiveReturnAuthorizationTuple.loading}
            onClick={() => dispatch({ type: 'RESET' })}>
            Start Over
          </Button>
        </Box>

        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>
                  Scanned?
                </TableCell>

                <TableCell>
                  IMEI
                </TableCell>

                <TableCell>
                  Item
                </TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {formState.lines.map(field => (
                <TableRow key={field.returnAuthorizationId}>
                  <TableCell>
                    {field.isReceived ? <Check color="success" /> : <Close color="error" />}

                  </TableCell>

                  <TableCell sx={{ fontSize: 12 }}>
                    {field.serialNumber}
                  </TableCell>

                  <TableCell sx={{ fontSize: 12 }}>
                    {field.item?.displayName}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </DialogContent>

      <DialogActions>
        <Button
          variant="outlined"
          disabled={receiveReturnAuthorizationTuple.loading}
          onClick={close}>
          Cancel
        </Button>

        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={!allReceived || receiveReturnAuthorizationTuple.loading}>
          Submit
        </Button>
      </DialogActions>
    </Dialog>
  );
};
